
import DOMPurify from 'dompurify';
import { format, parseISO } from 'date-fns';

export function formatPublicationDate(dateString: string | undefined): string {
  if (!dateString) {
    return 'No date available';
  }
  
  try {
    const date = parseISO(dateString);
    return format(date, 'MMMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date unavailable';
  }
}

export function sanitizeHtml(html: string): string {
  // Check if the content is a string
  if (typeof html !== 'string') {
    console.error('Invalid HTML content type:', typeof html);
    return '';
  }
  
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'ul', 'ol', 'li', 
      'b', 'i', 'strong', 'em', 'strike', 'code', 'hr', 'br', 'div', 
      'img', 'blockquote', 'pre', 'span', 'table', 'thead', 'tbody', 
      'tr', 'th', 'td', 'iframe', 'figure', 'figcaption', 'video'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel', 'id', 'style', 'width', 'height']
  });
}

export function renderRichTextContent(content: any): string {
  // If content is already a string, sanitize and return it
  if (typeof content === 'string') {
    // Process the HTML string to ensure proper list rendering
    let processedHtml = content;
    
    // Ensure lists are properly formatted with correct HTML structure
    // Sometimes CMS systems output lists without proper nesting
    processedHtml = processedHtml
      .replace(/<li>(.*?)<\/li>\s*(?!<\/[ou]l>|<li>)/g, '<li>$1</li></ul><ul>')
      .replace(/^<li>/g, '<ul><li>')
      .replace(/<\/li>$/g, '</li></ul>');
    
    // Fix any doubled-up list tags
    processedHtml = processedHtml
      .replace(/<\/ul><ul>/g, '')
      .replace(/<ul><\/ul>/g, '');
    
    return sanitizeHtml(processedHtml);
  }
  
  // Check if content is null or undefined
  if (!content) {
    console.error('Content is null or undefined');
    return '';
  }

  try {
    // If content is an object with blocks (EditorJS format)
    if (content && typeof content === 'object' && content.blocks) {
      console.log('Processing EditorJS format');
      return renderEditorJsContent(content);
    }
    
    // Try to stringify the content and sanitize it
    const contentString = JSON.stringify(content);
    console.log('Content stringified:', contentString.substring(0, 100));
    
    // Check if it's a JSON string that might be parsed
    try {
      const parsed = JSON.parse(contentString);
      if (parsed && typeof parsed === 'object' && parsed.blocks) {
        console.log('Found blocks in parsed content');
        return renderEditorJsContent(parsed);
      }
    } catch (e) {
      // Not valid JSON or already parsed
      console.log('Not valid JSON or already parsed:', e);
    }
    
    // Last resort: try to extract text content if it's an object
    if (typeof content === 'object') {
      // Try to find any text property
      for (const key of ['text', 'html', 'content']) {
        if (content[key] && typeof content[key] === 'string') {
          console.log(`Found ${key} property in content`);
          return sanitizeHtml(content[key]);
        }
      }
      
      // If we get here, we have an object but no recognizable text content
      // Just render as plain text with proper spacing
      const plainText = JSON.stringify(content, null, 2)
        .replace(/["{}:,\[\]]/g, '')  // Remove JSON syntax
        .replace(/\s{2,}/g, ' ')      // Replace multiple spaces with a single space
        .trim();
        
      return `<p>${plainText}</p>`;
    }
    
    // Fallback: sanitize the stringified content
    return `<p>${sanitizeHtml(String(content))}</p>`;
  } catch (error) {
    console.error('Error rendering rich text content:', error);
    return '<p>Error displaying content</p>';
  }
}

// Helper function to render EditorJS content with improved list handling
function renderEditorJsContent(content: any): string {
  if (!content.blocks || !Array.isArray(content.blocks)) {
    return '<p>Invalid content structure</p>';
  }

  let html = '';
  
  content.blocks.forEach((block: any) => {
    switch (block.type) {
      case 'header':
        const level = block.data.level || 2;
        html += `<h${level}>${block.data.text}</h${level}>`;
        break;
      case 'paragraph':
        html += `<p>${block.data.text}</p>`;
        break;
      case 'list':
        const listType = block.data.style === 'ordered' ? 'ol' : 'ul';
        html += `<${listType}>`;
        block.data.items.forEach((item: string) => {
          html += `<li>${item}</li>`;
        });
        html += `</${listType}>`;
        break;
      case 'image':
        html += `
          <figure>
            <img src="${block.data.file?.url || block.data.url}" alt="${block.data.caption || ''}" />
            ${block.data.caption ? `<figcaption>${block.data.caption}</figcaption>` : ''}
          </figure>
        `;
        break;
      case 'quote':
        html += `<blockquote>${block.data.text}</blockquote>`;
        if (block.data.caption) {
          html += `<cite>${block.data.caption}</cite>`;
        }
        break;
      case 'code':
        html += `<pre><code>${block.data.code}</code></pre>`;
        break;
      case 'delimiter':
        html += '<hr />';
        break;
      case 'table':
        if (block.data && block.data.content) {
          html += '<table>';
          // Add header row if it exists
          if (block.data.withHeadings && block.data.content.length > 0) {
            html += '<thead><tr>';
            block.data.content[0].forEach((cell: string) => {
              html += `<th>${cell}</th>`;
            });
            html += '</tr></thead>';
            // Start table body after the header
            html += '<tbody>';
            // Skip the first row as it's the header
            for (let i = 1; i < block.data.content.length; i++) {
              html += '<tr>';
              block.data.content[i].forEach((cell: string) => {
                html += `<td>${cell}</td>`;
              });
              html += '</tr>';
            }
          } else {
            // No header, all rows are body rows
            html += '<tbody>';
            block.data.content.forEach((row: string[]) => {
              html += '<tr>';
              row.forEach((cell: string) => {
                html += `<td>${cell}</td>`;
              });
              html += '</tr>';
            });
          }
          html += '</tbody></table>';
        } else {
          html += '<p>[Table content missing]</p>';
        }
        break;
      default:
        // For unknown block types, try to extract text if available
        if (block.data && block.data.text) {
          html += `<p>${block.data.text}</p>`;
        } else {
          html += `<p>[${block.type} block]</p>`;
        }
    }
  });
  
  return sanitizeHtml(html);
}

export function generateArticleSchema(post: any) {
  // Get the publication date from either publicationDate or publicationdate field
  const pubDate = post.publicationDate || post.publicationdate;
  const currentUrl = `https://astrem.co/blog/${post.slug}`;
  
  // Create base schema with required fields
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title || "Blog Post",
    "description": post.excerpt || post.title || "Blog post from Astrem",
    "url": currentUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "author": {
      "@type": "Person",
      "name": "Evan Rabalais",
      "url": "https://astrem.co"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Astrem",
      "url": "https://astrem.co",
      "logo": {
        "@type": "ImageObject",
        "url": "https://astrem.co/lovable-uploads/d023512a-50be-4738-9760-d5afcf453819.png",
        "width": 512,
        "height": 512
      }
    }
  };

  // Add publication date if available
  if (pubDate) {
    try {
      const formattedDate = new Date(pubDate).toISOString();
      schema["datePublished"] = formattedDate;
      schema["dateModified"] = formattedDate;
    } catch (error) {
      console.error('Error formatting publication date for schema:', error);
    }
  }

  // Add featured image if available
  if (post.featuredImage) {
    let imageUrl = '';
    
    // Handle different featuredImage formats
    if (typeof post.featuredImage === 'string') {
      // If it's a string, assume it's an ID
      imageUrl = `https://api.flotiq.com/image/1200x630/${post.featuredImage}.webp`;
    } else if (post.featuredImage.id) {
      // If it's an object with id
      imageUrl = `https://api.flotiq.com/image/1200x630/${post.featuredImage.id}.webp`;
    } else if (post.featuredImage[0]?.id) {
      // If it's an array with objects containing id
      imageUrl = `https://api.flotiq.com/image/1200x630/${post.featuredImage[0].id}.webp`;
    }
    
    if (imageUrl) {
      schema["image"] = {
        "@type": "ImageObject",
        "url": imageUrl,
        "width": 1200,
        "height": 630
      };
    }
  }

  // Add thumbnail/fallback image if no featured image
  if (!schema["image"] && post.thumbnail && post.thumbnail[0]?.url) {
    schema["image"] = {
      "@type": "ImageObject",
      "url": post.thumbnail[0].url,
      "width": 1200,
      "height": 630
    };
  }

  // Return the schema object (NOT stringified)
  return schema;
}
